import moment from 'moment-timezone';
import {i18n} from 'src/i18n';
import type {ExcelTableBlock, ExcelTableHeaderCell} from 'src/modules/shared/excel/excel';
import {Excel, ExcelWorksheetBuilder, normalizeExcelFileNamePart} from 'src/modules/shared/excel/excel';
import type {ParentFieldLayoutConfig, ParentFieldQuestion} from 'src/modules/tarea/parentFieldLayout';
import {normalizeParentFieldLayoutConfig, resolveParentFieldsForRender} from 'src/modules/tarea/parentFieldLayout';
import {
  buildParcialGroups,
  buildSubsectionHeaderSpans,
  getChildTableRowLabel,
  getExecutorSignatureColumnLabel,
  getParcialAnswerText,
  getRecordId,
  getSignaturePersonDisplayName,
  hasSubsectionHeaderSpans,
  isChildTasksWideTableMode,
} from 'src/modules/tarea/tareaParcialHelpers';
import {getTaskReportFolio} from 'src/modules/tarea/taskVisualLabel';

const SIDE_BY_SIDE_MAX_COLUMNS = 6;
const TABLE_GAP_COLUMNS = 2;

type ChildTaskExcelSection = {
  key?: string;
  title: string;
  tasks: any[];
};

type ChildTaskExcelParams = {
  parentTask: any;
  tasks: any[];
  parentQuestions: ParentFieldQuestion[];
  parentQuestionKeys: ParentFieldLayoutConfig;
  sections?: ChildTaskExcelSection[];
  tenantId?: string | null;
  title?: string;
};

type ChildTaskExcelTable = ExcelTableBlock & {
  questionColumnCount: number;
};

type PositionedTableRow = {
  tables: ChildTaskExcelTable[];
};

const getSignatureSource = (signature: any) =>
  signature?.firma || signature?.firmaRef?.downloadUrl || signature?.firmaRef?.publicUrl || '';

const getExecutorSignature = (task: any) => {
  const executorId = getRecordId(task?.ejecutor);
  const signed = (task?.firmas || []).filter((signature) => signature?.firmada && getSignatureSource(signature));
  return signed.find((signature) => executorId && getRecordId(signature?.responsable) === executorId);
};

const getSignatureText = (task: any) => {
  const signature = getExecutorSignature(task);
  if (!signature) return '-';
  return getSignaturePersonDisplayName(signature?.responsable) || getSignaturePersonDisplayName(task?.ejecutor) || '-';
};

const buildTaskTable = (group: any, tenantId: string | null | undefined, wideMode: boolean) => {
  const includeSignature = group.inspecciones.some((task) => Boolean(getSignatureSource(getExecutorSignature(task))));
  const showSubsectionHeaders = wideMode && hasSubsectionHeaderSpans(group.columns);
  let headers: ExcelTableHeaderCell[][];

  if (showSubsectionHeaders) {
    const subsectionSpans = buildSubsectionHeaderSpans(group.columns);
    headers = [
      [
        {value: i18n('entities.firma.parcial.number'), rowSpan: 2},
        ...subsectionSpans.map((span) => ({
          value: span.label,
          colSpan: span.colSpan,
        })),
        ...(includeSignature
          ? [
              {
                value: getExecutorSignatureColumnLabel(tenantId),
                rowSpan: 2,
              },
            ]
          : []),
      ],
      group.columns.map((column) => column.label),
    ];
  } else {
    headers = [
      [
        i18n('entities.firma.parcial.number'),
        ...group.columns.map((column) => column.label),
        ...(includeSignature ? [getExecutorSignatureColumnLabel(tenantId)] : []),
      ],
    ];
  }

  return {
    title: group.modelo?.titulo || i18n('entities.tarea.answerTableUntypedGroup'),
    questionColumnCount: group.columns.length,
    headers,
    rows: group.inspecciones.map((task, index) => [
      getChildTableRowLabel(task, index, tenantId),
      ...group.columns.map((column) => getParcialAnswerText(task, column, '-', {tenantId})),
      ...(includeSignature ? [getSignatureText(task)] : []),
    ]),
  };
};

const buildPositionedTableRows = (tables: ChildTaskExcelTable[], wideMode: boolean): PositionedTableRow[] => {
  if (!wideMode) {
    return tables.map((table) => ({tables: [table]}));
  }

  const fullWidthTables = tables.filter((table) => table.questionColumnCount > SIDE_BY_SIDE_MAX_COLUMNS);
  const compactTables = tables.filter((table) => table.questionColumnCount <= SIDE_BY_SIDE_MAX_COLUMNS);
  const rows = fullWidthTables.map((table) => ({tables: [table]}));

  for (let index = 0; index < compactTables.length; index += 2) {
    rows.push({tables: compactTables.slice(index, index + 2)});
  }
  return rows;
};

const addParentFields = (
  builder: ExcelWorksheetBuilder,
  questions: ParentFieldQuestion[],
  layoutRaw: ParentFieldLayoutConfig,
  startRow: number,
) => {
  const config = normalizeParentFieldLayoutConfig(layoutRaw);
  const fields = resolveParentFieldsForRender(questions || [], config.fields, {
    whenEmptyUseAll: false,
    cellsPerRow: config.cellsPerRow,
  });
  const rows = fields.map((field) => [field.label, field.answer || '-']);
  builder.addRows(rows, startRow);
  builder.fitColumns(rows, 0, 10, 48);
  return startRow + rows.length;
};

export function exportChildTasksTableAsExcel(params: ChildTaskExcelParams) {
  const populatedSections = params.sections?.filter((section) => section.tasks?.length) || [];
  const sections = populatedSections.length
    ? populatedSections
    : [
        {
          key: 'all',
          title: params.title || i18n('entities.tarea.childTasksListTitle'),
          tasks: params.tasks,
        },
      ];
  const groupsBySection = sections.map((section) => ({
    section,
    groups: buildParcialGroups(section.tasks || []),
  }));
  const maxColumnCount = groupsBySection.reduce(
    (max, item) => Math.max(max, ...item.groups.map((group) => group.columns.length), 0),
    0,
  );
  const wideMode = isChildTasksWideTableMode(params.tenantId, maxColumnCount);
  const sectionLayouts = groupsBySection.map(({section, groups}) => {
    const tables = groups.map((group) => buildTaskTable(group, params.tenantId, wideMode));
    return {
      section,
      rows: buildPositionedTableRows(tables, wideMode),
    };
  });
  const builder = new ExcelWorksheetBuilder();
  const parentTitle = params.parentTask?.tituloTarea || params.parentTask?.titulo || params.parentTask?.modeloTarea?.titulo || '';
  const parentNumber = getTaskReportFolio(params.parentTask);
  const displayTitle = [parentNumber, parentTitle].filter(Boolean).join(' - ');
  let currentRow = 0;

  builder.addRows([[displayTitle || params.title || i18n('entities.tarea.answerTablePdfTitle')]], currentRow);
  // currentRow += 1;
  // builder.addRows([[params.title || i18n('entities.tarea.answerTablePdfTitle')]], currentRow);
  currentRow += 2;
  currentRow = addParentFields(builder, params.parentQuestions, params.parentQuestionKeys, currentRow);
  currentRow += 1;

  sectionLayouts.forEach(({section, rows}) => {
    const showSectionTitle = section.key !== 'all' && section.title !== i18n('entities.tarea.childTasksListTitle');
    if (showSectionTitle) {
      currentRow += 1;
      builder.addRows([[section.title]], currentRow);
      currentRow += 2;
    }

    rows.forEach((positionedRow) => {
      let startColumn = 0;
      let endRow = currentRow;
      positionedRow.tables.forEach((table) => {
        const bounds = builder.addTable(table, currentRow, startColumn);
        endRow = Math.max(endRow, bounds.endRow);
        startColumn += bounds.width + TABLE_GAP_COLUMNS;
      });
      currentRow = endRow + 2;
    });
  });

  const worksheet = builder.build();
  const fileNumber = normalizeExcelFileNamePart(
    parentNumber || getRecordId(params.parentTask) || moment().format('YYYYMMDD-HHmmss'),
  );
  const fileTitle = normalizeExcelFileNamePart(parentTitle, {
    lowercase: true,
    separator: '-',
  });
  const fileName = [fileNumber, fileTitle].filter(Boolean).join('_');
  Excel.exportWorksheetAsExcelFile(
    worksheet,
    params.title || i18n('entities.tarea.answerTablePdfTitle'),
    fileName || 'registro-tabulado',
  );
}
